import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { isApiErrorEnvelope } from '@/api/form-errors'
import { useHelpRequests } from '@/help/api'
import { useUserPhotos } from '@/photos/api'
import {
  useFixBillingData,
  useGdprDeletionCancel,
  useGdprDeletionRequest,
  useGdprExportRequest,
  useGrantCompPremium,
  useResendVerification,
  useRevokeCompPremium,
  useSendPasswordResetEmail,
  useUserDetail,
  type FixBillingDataFields,
} from '@/users/api'
import type { AdminCheckoutSession, AdminUserDetail } from '@/users/schemas'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

function formatIso(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('hu-HU')
  } catch {
    return '—'
  }
}

function formatHuf(value: number | undefined): string {
  return typeof value === 'number' ? `${value.toLocaleString('hu-HU')} Ft` : '—'
}

/** Nem-fizetett (adminból visszavonható) providerek — a backend szabálya. */
const REVOCABLE_PROVIDERS = ['admin_comp', 'founder_grant']

/**
 * `/users/{ulid}` — felhasználó-részletlap (DEC-ADMIN-014): fiók + profil +
 * DÁP/founder + fotók (a meglévő photos-endpointtal) + billing-blokk
 * (aktív előfizetés, kompenzációs prémium grant/revoke, checkout-sessionök a
 * bizonylatokkal + számlázásiadat-javítás a failed/needs_billing_data számlára).
 */
export function UserDetail() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const detail = useUserDetail(id ?? null)
  const photos = useUserPhotos(id ?? null)
  const [error, setError] = useState<string | null>(null)

  function onMutationError(fallbackKey: string) {
    return (mutationError: unknown) => {
      if (isApiErrorEnvelope(mutationError)) {
        setError(mutationError.message)
      } else {
        setError(t(fallbackKey))
      }
    }
  }

  const user = detail.data

  return (
    <main className="mx-auto flex max-w-3xl flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <Link
          to="/users"
          className="text-sm text-primary underline-offset-4 hover:underline self-start"
        >
          {t('userDetail.back')}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">
          {user?.profile?.nick_name || user?.email || t('userDetail.title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('userDetail.intro')}</p>
      </header>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {detail.isPending && <p className="text-muted-foreground">{t('common.loading')}</p>}
      {detail.isError && (
        <Alert variant="destructive">
          <AlertDescription>{t('userDetail.loadError')}</AlertDescription>
        </Alert>
      )}

      {user && (
        <>
          <section className="flex flex-col gap-2 rounded-lg border p-4">
            <h2 className="text-lg font-semibold">{t('userDetail.account.title')}</h2>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
              {/* mailto — a support-kontakt egy kattintás (DEC-ADMIN-015 kieg.). */}
              <div className="flex justify-between gap-2 sm:block">
                <dt className="text-muted-foreground">{t('userDetail.account.email')}</dt>
                <dd>
                  {user.email ? (
                    <a
                      href={`mailto:${user.email}`}
                      className="text-primary underline underline-offset-4"
                    >
                      {user.email}
                    </a>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <DetailRow
                label={t('userDetail.account.status')}
                value={t(`users.status${statusSuffix(user.status)}`)}
              />
              <DetailRow label={t('users.createdAtLabel')} value={formatIso(user.created_at)} />
              <DetailRow label={t('users.lastLoginLabel')} value={formatIso(user.last_login_at)} />
              <DetailRow
                label={t('userDetail.account.emailVerifiedAt')}
                value={formatIso(user.email_verified_at)}
              />
            </dl>
          </section>

          {user.abuse_ban?.active && (
            <section className="flex flex-col gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
              <h2 className="text-lg font-semibold text-destructive">
                {t('userDetail.abuseBan.title')}
              </h2>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                <DetailRow
                  label={t('userDetail.abuseBan.reason')}
                  value={user.abuse_ban.reason ?? '—'}
                />
                <DetailRow
                  label={t('userDetail.abuseBan.bannedAt')}
                  value={formatIso(user.abuse_ban.banned_at)}
                />
                <DetailRow
                  label={t('userDetail.abuseBan.expiresAt')}
                  value={formatIso(user.abuse_ban.expires_at)}
                />
              </dl>
            </section>
          )}

          <section className="flex flex-col gap-2 rounded-lg border p-4">
            <h2 className="text-lg font-semibold">{t('userDetail.profile.title')}</h2>
            {user.profile ? (
              <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                <DetailRow
                  label={t('userDetail.profile.nickName')}
                  value={user.profile.nick_name ?? '—'}
                />
                <DetailRow
                  label={t('userDetail.profile.birthYear')}
                  value={user.profile.birth_year?.toString() ?? '—'}
                />
                <DetailRow
                  label={t('userDetail.profile.gender')}
                  value={user.profile.gender ?? '—'}
                />
                <DetailRow label={t('userDetail.profile.city')} value={user.profile.city ?? '—'} />
                <DetailRow
                  label={t('userDetail.profile.visibility')}
                  value={user.profile.visibility ?? '—'}
                />
                <DetailRow
                  label={t('users.lastActiveLabel')}
                  value={formatIso(user.profile.last_active_at)}
                />
                {user.profile.bio && (
                  <div className="sm:col-span-2">
                    <dt className="text-muted-foreground">{t('userDetail.profile.bio')}</dt>
                    <dd className="whitespace-pre-wrap">{user.profile.bio}</dd>
                  </div>
                )}
              </dl>
            ) : (
              <p className="text-sm text-muted-foreground">{t('userDetail.profile.missing')}</p>
            )}
          </section>

          <section className="flex flex-col gap-2 rounded-lg border p-4">
            <h2 className="text-lg font-semibold">{t('userDetail.dap.title')}</h2>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
              <DetailRow
                label={t('userDetail.dap.verified')}
                value={user.dap?.verified ? t('common.yes') : t('common.no')}
              />
              <DetailRow
                label={t('userDetail.dap.verifiedAt')}
                value={formatIso(user.dap?.verified_at)}
              />
              <DetailRow
                label={t('userDetail.dap.firstName')}
                value={user.dap?.first_name ?? '—'}
              />
              <DetailRow
                label={t('userDetail.dap.founderRank')}
                value={user.dap?.founder_rank?.toString() ?? '—'}
              />
            </dl>
          </section>

          <section className="flex flex-col gap-2 rounded-lg border p-4">
            <h2 className="text-lg font-semibold">{t('userDetail.photos.title')}</h2>
            {photos.isPending && <p className="text-muted-foreground">{t('common.loading')}</p>}
            {!photos.isPending && (photos.data?.length ?? 0) === 0 && (
              <p className="text-sm text-muted-foreground">{t('userDetail.photos.empty')}</p>
            )}
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {(photos.data ?? []).map((photo) => (
                <figure key={photo.id} className="flex flex-col gap-1">
                  {photo.thumbnail_url || photo.url ? (
                    <img
                      src={photo.thumbnail_url ?? photo.url ?? undefined}
                      alt={photo.status ?? ''}
                      className="aspect-square rounded-md border object-cover"
                    />
                  ) : (
                    <div className="aspect-square rounded-md border bg-muted" />
                  )}
                  <figcaption className="text-xs text-muted-foreground">{photo.status}</figcaption>
                </figure>
              ))}
            </div>
          </section>

          <SupportSection user={user} userId={id ?? ''} onError={onMutationError} />
          <SubscriptionSection user={user} userId={id ?? ''} onError={onMutationError} />
          <PurchasesSection user={user} onError={onMutationError} />
        </>
      )}
    </main>
  )
}

function statusSuffix(status: AdminUserDetail['status']): string {
  switch (status) {
    case 'pending_email':
      return 'PendingEmail'
    case 'disabled':
      return 'Disabled'
    case 'deleted':
      return 'Deleted'
    default:
      return 'Active'
  }
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2 sm:block">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}

/**
 * DEC-ADMIN-015 — read-only kontextus (darabszámok tartalom nélkül + referral +
 * GDPR-állapotok) és support-akciók (e-mail-újraküldés, jelszó-reset link,
 * GDPR érintetti kérelemre — az indok a naplóba kerül).
 */
function SupportSection({
  user,
  userId,
  onError,
}: {
  user: AdminUserDetail
  userId: string
  onError: (fallbackKey: string) => (error: unknown) => void
}) {
  const { t } = useTranslation()
  const resend = useResendVerification()
  const pwdReset = useSendPasswordResetEmail()
  const gdprRequest = useGdprDeletionRequest()
  const gdprCancel = useGdprDeletionCancel()
  const gdprExport = useGdprExportRequest()
  // A user segítségkérései bejelentőként VAGY érintettként (DEC-ADMIN-015 kieg.).
  const helpRequests = useHelpRequests({ user: userId })
  const [gdprReason, setGdprReason] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  const support = user.support
  const deletionLive =
    support?.deletion_request?.status === 'requested' ||
    support?.deletion_request?.status === 'scheduled'
  const gdprDisabled = gdprReason.trim().length < 3

  function onDone(noticeKey: string) {
    return () => {
      setNotice(t(noticeKey))
      setGdprReason('')
    }
  }

  return (
    <section className="flex flex-col gap-3 rounded-lg border p-4">
      <h2 className="text-lg font-semibold">{t('userDetail.support.title')}</h2>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <DetailRow
          label={t('userDetail.support.helpRequests')}
          value={`${support?.help_requests_as_requester ?? 0} / ${support?.help_requests_as_subject ?? 0}`}
        />
        <DetailRow
          label={t('userDetail.support.blocks')}
          value={`${support?.blocks_given ?? 0} / ${support?.blocks_received ?? 0}`}
        />
        <DetailRow
          label={t('userDetail.support.referral')}
          value={t('userDetail.support.referralValue', {
            invited: support?.referral?.invited_count ?? 0,
            qualified: support?.referral?.qualified_count ?? 0,
            credit: (support?.referral?.credit_balance_huf ?? 0).toLocaleString('hu-HU'),
          })}
        />
        <DetailRow
          label={t('userDetail.support.deletionRequest')}
          value={
            support?.deletion_request
              ? `${support.deletion_request.status ?? ''} (${formatIso(support.deletion_request.scheduled_for)})`
              : '—'
          }
        />
        <DetailRow
          label={t('userDetail.support.latestExport')}
          value={
            support?.latest_export
              ? `${support.latest_export.status ?? ''} (${formatIso(support.latest_export.requested_at)})`
              : '—'
          }
        />
      </dl>
      {/* A user segítségkéréseinek beágyazott listája (kezelés a felületen). */}
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium">{t('userDetail.support.helpListTitle')}</p>
        {helpRequests.isPending && (
          <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
        )}
        {!helpRequests.isPending && (helpRequests.data?.items.length ?? 0) === 0 && (
          <p className="text-xs text-muted-foreground">{t('userDetail.support.helpListEmpty')}</p>
        )}
        {(helpRequests.data?.items ?? []).map((row) => (
          <div
            key={row.id}
            className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded bg-muted/30 px-2 py-1 text-xs"
          >
            <span className="font-medium">#{row.id}</span>
            <span>{row.context_page ? t(`helpRequests.page.${row.context_page}`) : '—'}</span>
            <span className="rounded-full bg-secondary px-2 py-0.5">
              {row.status ? t(`helpRequests.status.${row.status}`) : '—'}
            </span>
            <span className="text-muted-foreground">
              {row.requester_user_id === userId
                ? t('userDetail.support.roleRequester')
                : t('userDetail.support.roleSubject')}
            </span>
            <span className="break-all">{row.message}</span>
            <span className="text-muted-foreground">{formatIso(row.created_at)}</span>
          </div>
        ))}
        <Link
          to="/help-requests"
          className="self-start text-xs text-primary underline underline-offset-4"
        >
          {t('userDetail.support.helpRequestsLink')}
        </Link>
      </div>

      {notice && <p className="text-sm text-verified">{notice}</p>}

      <div className="flex flex-wrap gap-2">
        {!user.email_verified_at && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={resend.isPending}
            onClick={() => {
              if (!window.confirm(t('userDetail.support.resendConfirm'))) return
              setNotice(null)
              resend.mutate(
                { id: userId },
                {
                  onError: onError('userDetail.support.actionError'),
                  onSuccess: onDone('userDetail.support.resendDone'),
                },
              )
            }}
          >
            {t('userDetail.support.resendButton')}
          </Button>
        )}
        {user.status === 'active' && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={pwdReset.isPending}
            onClick={() => {
              if (!window.confirm(t('userDetail.support.pwdResetConfirm'))) return
              setNotice(null)
              pwdReset.mutate(
                { id: userId },
                {
                  onError: onError('userDetail.support.actionError'),
                  onSuccess: onDone('userDetail.support.pwdResetDone'),
                },
              )
            }}
          >
            {t('userDetail.support.pwdResetButton')}
          </Button>
        )}
      </div>

      {/* GDPR-akciók érintetti kérelemre — közös, kötelező indokkal. */}
      <div className="flex flex-col gap-2 rounded-md bg-muted/30 p-3">
        <p className="text-sm font-medium">{t('userDetail.support.gdprTitle')}</p>
        <p className="text-xs text-muted-foreground">{t('userDetail.support.gdprHint')}</p>
        <Input
          value={gdprReason}
          onChange={(e) => setGdprReason(e.target.value)}
          placeholder={t('userDetail.support.gdprReasonPlaceholder')}
          aria-label={t('userDetail.support.gdprReasonLabel')}
        />
        <div className="flex flex-wrap gap-2">
          {!deletionLive && user.status !== 'deleted' && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={gdprRequest.isPending || gdprDisabled}
              onClick={() => {
                setNotice(null)
                gdprRequest.mutate(
                  { id: userId, reason: gdprReason.trim() },
                  {
                    onError: onError('userDetail.support.actionError'),
                    onSuccess: onDone('userDetail.support.gdprRequestDone'),
                  },
                )
              }}
            >
              {t('userDetail.support.gdprRequestButton')}
            </Button>
          )}
          {deletionLive && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={gdprCancel.isPending || gdprDisabled}
              onClick={() => {
                setNotice(null)
                gdprCancel.mutate(
                  { id: userId, reason: gdprReason.trim() },
                  {
                    onError: onError('userDetail.support.actionError'),
                    onSuccess: onDone('userDetail.support.gdprCancelDone'),
                  },
                )
              }}
            >
              {t('userDetail.support.gdprCancelButton')}
            </Button>
          )}
          {user.status !== 'deleted' && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={gdprExport.isPending || gdprDisabled}
              onClick={() => {
                setNotice(null)
                gdprExport.mutate(
                  { id: userId, reason: gdprReason.trim() },
                  {
                    onError: onError('userDetail.support.actionError'),
                    onSuccess: onDone('userDetail.support.gdprExportDone'),
                  },
                )
              }}
            >
              {t('userDetail.support.gdprExportButton')}
            </Button>
          )}
        </div>
      </div>
    </section>
  )
}

function SubscriptionSection({
  user,
  userId,
  onError,
}: {
  user: AdminUserDetail
  userId: string
  onError: (fallbackKey: string) => (error: unknown) => void
}) {
  const { t } = useTranslation()
  const grant = useGrantCompPremium()
  const revoke = useRevokeCompPremium()
  const [months, setMonths] = useState('1')
  const [grantReason, setGrantReason] = useState('')
  const [revokeReason, setRevokeReason] = useState('')

  const active = user.billing?.active_subscription ?? null
  const revocable = active != null && REVOCABLE_PROVIDERS.includes(active.provider ?? '')

  return (
    <section className="flex flex-col gap-3 rounded-lg border p-4">
      <h2 className="text-lg font-semibold">{t('userDetail.subscription.title')}</h2>

      {active ? (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          <DetailRow label={t('userDetail.subscription.plan')} value={active.plan ?? '—'} />
          <DetailRow label={t('userDetail.subscription.provider')} value={active.provider ?? '—'} />
          <DetailRow
            label={t('userDetail.subscription.periodStart')}
            value={formatIso(active.current_period_start)}
          />
          <DetailRow
            label={t('userDetail.subscription.periodEnd')}
            value={formatIso(active.current_period_end)}
          />
        </dl>
      ) : (
        <p className="text-sm text-muted-foreground">{t('userDetail.subscription.none')}</p>
      )}

      {/* Kompenzációs prémium (DEC-ADMIN-014): indok kötelező, naplózva. */}
      <div className="flex flex-col gap-2 rounded-md bg-muted/30 p-3">
        <p className="text-sm font-medium">{t('userDetail.comp.grantTitle')}</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <select
            className="rounded-md border bg-background px-2 py-1.5 text-sm"
            value={months}
            onChange={(e) => setMonths(e.target.value)}
            aria-label={t('userDetail.comp.monthsLabel')}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {t('userDetail.comp.monthsOption', { months: m })}
              </option>
            ))}
          </select>
          <Input
            value={grantReason}
            onChange={(e) => setGrantReason(e.target.value)}
            placeholder={t('users.reasonPlaceholder')}
            aria-label={t('userDetail.comp.grantReasonLabel')}
          />
          <Button
            type="button"
            size="sm"
            disabled={grant.isPending || grantReason.trim().length < 3}
            onClick={() =>
              grant.mutate(
                { id: userId, months: Number(months), reason: grantReason.trim() },
                {
                  onError: onError('userDetail.comp.grantError'),
                  onSuccess: () => setGrantReason(''),
                },
              )
            }
          >
            {t('userDetail.comp.grantButton')}
          </Button>
        </div>

        {active && (
          <div className="flex flex-col gap-2 border-t pt-2 sm:flex-row">
            {revocable ? (
              <>
                <Input
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  placeholder={t('users.reasonPlaceholder')}
                  aria-label={t('userDetail.comp.revokeReasonLabel')}
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={revoke.isPending || revokeReason.trim().length < 3}
                  onClick={() =>
                    revoke.mutate(
                      { id: userId, reason: revokeReason.trim() },
                      {
                        onError: onError('userDetail.comp.revokeError'),
                        onSuccess: () => setRevokeReason(''),
                      },
                    )
                  }
                >
                  {t('userDetail.comp.revokeButton')}
                </Button>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">{t('userDetail.comp.paidNote')}</p>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

function PurchasesSection({
  user,
  onError,
}: {
  user: AdminUserDetail
  onError: (fallbackKey: string) => (error: unknown) => void
}) {
  const { t } = useTranslation()
  const sessions = user.billing?.checkout_sessions ?? []

  return (
    <section className="flex flex-col gap-3 rounded-lg border p-4">
      <h2 className="text-lg font-semibold">{t('userDetail.purchases.title')}</h2>
      {sessions.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('userDetail.purchases.empty')}</p>
      )}
      {sessions.map((session) => (
        <SessionCard key={session.id} session={session} onError={onError} />
      ))}
    </section>
  )
}

function SessionCard({
  session,
  onError,
}: {
  session: AdminCheckoutSession
  onError: (fallbackKey: string) => (error: unknown) => void
}) {
  const { t } = useTranslation()
  const fix = useFixBillingData()
  const [fixOpen, setFixOpen] = useState(false)
  const [fields, setFields] = useState({
    billing_name: '',
    billing_post_code: '',
    billing_city: '',
    billing_address: '',
    billing_taxcode: '',
  })
  const [clearTaxcode, setClearTaxcode] = useState(false)

  const documents = session.documents ?? []
  // Adatjavítás csak a nem-kiállt SZÁMLÁRA értelmes (DEC-ADMIN-014).
  const fixableDocument = documents.find(
    (document) =>
      document.kind === 'invoice' &&
      (document.status === 'failed' || document.status === 'needs_billing_data'),
  )

  function onFixSubmit() {
    if (session.id == null) return
    const payload: FixBillingDataFields = {}
    if (fields.billing_name.trim() !== '') payload.billing_name = fields.billing_name.trim()
    if (fields.billing_post_code.trim() !== '')
      payload.billing_post_code = fields.billing_post_code.trim()
    if (fields.billing_city.trim() !== '') payload.billing_city = fields.billing_city.trim()
    if (fields.billing_address.trim() !== '')
      payload.billing_address = fields.billing_address.trim()
    if (clearTaxcode) {
      payload.billing_taxcode = ''
    } else if (fields.billing_taxcode.trim() !== '') {
      payload.billing_taxcode = fields.billing_taxcode.trim()
    }
    fix.mutate(
      { sessionId: session.id, fields: payload },
      {
        onError: onError('userDetail.fix.error'),
        onSuccess: () => setFixOpen(false),
      },
    )
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border p-3 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-medium">
          {t('userDetail.purchases.months', { months: session.months ?? 0 })} ·{' '}
          {formatHuf(session.amount_huf)}
        </span>
        <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{session.provider}</span>
        <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{session.status}</span>
        {session.wants_invoice && (
          <span className="text-xs text-muted-foreground">
            {t('userDetail.purchases.wantsInvoice')}
          </span>
        )}
        <span className="text-xs text-muted-foreground">{formatIso(session.created_at)}</span>
      </div>

      {(session.documents ?? []).map((document, index) => (
        <div
          key={`${document.kind}-${index}`}
          className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded bg-muted/30 px-2 py-1 text-xs"
        >
          <span className="font-medium">
            {t(document.kind === 'invoice' ? 'userDetail.doc.invoice' : 'userDetail.doc.receipt')}
          </span>
          <span>{t(`userDetail.doc.status.${document.status ?? 'pending'}`)}</span>
          {document.external_id && <span>#{document.external_id}</span>}
          {typeof document.attempts === 'number' && document.attempts > 0 && (
            <span>{t('userDetail.doc.attempts', { attempts: document.attempts })}</span>
          )}
          {document.last_error && <span className="text-destructive">{document.last_error}</span>}
          {document.issued_at && <span>{formatIso(document.issued_at)}</span>}
        </div>
      ))}

      {fixableDocument && !fixOpen && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => setFixOpen(true)}
        >
          {t('userDetail.fix.open')}
        </Button>
      )}

      {fixableDocument && fixOpen && (
        <div className="flex flex-col gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2">
          <p className="text-xs text-muted-foreground">{t('userDetail.fix.hint')}</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Input
              value={fields.billing_name}
              onChange={(e) => setFields((f) => ({ ...f, billing_name: e.target.value }))}
              placeholder={t('userDetail.fix.name')}
              aria-label={t('userDetail.fix.name')}
            />
            <Input
              value={fields.billing_post_code}
              onChange={(e) => setFields((f) => ({ ...f, billing_post_code: e.target.value }))}
              placeholder={t('userDetail.fix.postCode')}
              aria-label={t('userDetail.fix.postCode')}
              inputMode="numeric"
              maxLength={4}
            />
            <Input
              value={fields.billing_city}
              onChange={(e) => setFields((f) => ({ ...f, billing_city: e.target.value }))}
              placeholder={t('userDetail.fix.city')}
              aria-label={t('userDetail.fix.city')}
            />
            <Input
              value={fields.billing_address}
              onChange={(e) => setFields((f) => ({ ...f, billing_address: e.target.value }))}
              placeholder={t('userDetail.fix.address')}
              aria-label={t('userDetail.fix.address')}
            />
            <Input
              value={fields.billing_taxcode}
              onChange={(e) => setFields((f) => ({ ...f, billing_taxcode: e.target.value }))}
              placeholder={t('userDetail.fix.taxcode')}
              aria-label={t('userDetail.fix.taxcode')}
              maxLength={13}
              disabled={clearTaxcode}
            />
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={clearTaxcode}
                onChange={(e) => setClearTaxcode(e.target.checked)}
              />
              {t('userDetail.fix.clearTaxcode')}
            </label>
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={onFixSubmit} disabled={fix.isPending}>
              {t('userDetail.fix.submit')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setFixOpen(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
